import { TPlant } from '@/features/plants/schemas/plants.schema.ts';
import { format } from 'date-fns';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { useState } from 'react';
import { PlantPowerFlow } from '@/assets/plant/power-flow.tsx';
import { GetPlantOverviewResponse } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-overview.ts';
import { Button } from '@/components/ui/button.tsx';
import { cn } from '@/utils/utils.ts';

const getPlantDetails = (plant: TPlant | undefined) => {
  if (!plant) return [];

  return [
    {
      label: 'Name',
      value: plant.basicDetails.name,
    },
    {
      label: 'Type',
      value: `${plant?.basicDetails.type}`,
    },
    {
      label: 'Address',
      value: `${plant?.siteInformation.country.value}, ${plant?.siteInformation.city.value}`,
    },
    {
      label: 'Connection date',
      value: format(new Date(plant?.createdAt), 'dd MMM yyyy'),
    },
    {
      label: 'Site area',
      value: `${plant?.siteInformation.siteArea} m²`,
    },
    {
      label: 'Capacity',
      value: `${plant?.technicalSpecifications.capacity} kW`,
    },
    {
      label: 'Storage capacity',
      value: `${plant?.technicalSpecifications.storageCapacity} kWh`,
    },
    {
      label: 'Tariff rate',
      value: `${plant?.financialInformation.tariffRate}€/kWh`,
    },
    {
      label: 'Owner email',
      value: plant?.contactDetails.ownerEmail,
    },
    {
      label: 'Installer email',
      value: plant?.contactDetails.installerEmail,
    },
  ];
};

const calculateMeasurements = (overview: GetPlantOverviewResponse | undefined | null) => {
  return {
    production: Number(overview?.currentYieldPower?.toFixed(2)),
    consumption: Number(overview?.currentConsumptionPower?.toFixed(2)),
    grid: Number(overview?.currentGridPower?.toFixed(2)),
    batteryPower: Number(overview?.currentBatteryPower?.toFixed(2)),
    batterySoC: Number(overview?.currentBatterySoC?.toFixed(1)),
  };
};

export const PlantProductionAndStorageFlowAndDetailsCard = ({
  plant,
  overview,
  isLoading,
  className,
}: {
  plant: TPlant | undefined;
  overview: GetPlantOverviewResponse | undefined | null;
  isLoading: boolean;
  className?: string;
}) => {
  const [tab, setTab] = useState<'power-flow' | 'plant-details'>('power-flow');

  const measurements = calculateMeasurements(overview);

  return (
    <DashboardCard className={cn('text-nowrap', className)} isLoading={isLoading}>
      <div className="flex flex-col gap-4">
        <div className="flex w-full justify-between">
          <h3 className="flex flex-col gap-1 font-semibold text-nowrap">
            {tab === 'power-flow' ? 'Power flow' : 'Plant details'}
          </h3>
          <Button
            variant="outline"
            onClick={() => setTab(tab === 'power-flow' ? 'plant-details' : 'power-flow')}
          >
            {tab === 'power-flow' ? 'Plant details' : 'Power Flow'}
          </Button>
        </div>
        <div className="relative flex min-w-[240px] flex-col gap-4">
          {tab === 'power-flow' && (
            <div className="flex w-full justify-between">
              <div className="flex h-full w-full items-center justify-center">
                <PlantPowerFlow
                  width="100%"
                  height="100%"
                  className="max-w-[26rem]"
                  measurements={measurements}
                />
              </div>
            </div>
          )}
          {tab === 'plant-details' &&
            getPlantDetails(plant).map((detail) => (
              <div key={detail.label} className="grid grid-cols-[minmax(120px,40%)_1fr] gap-4">
                <span className="text-muted-foreground">{detail.label}: </span>
                <span className="overflow-hidden font-medium text-ellipsis">
                  {detail.value}
                </span>
              </div>
            ))}
        </div>
      </div>
    </DashboardCard>
  );
};
